/**
 * Rango Quantum — renderer.
 *
 * Solo pinta: la detección vive en engine.js (vía adapter.js). Todo el
 * dibujo pasa por createChartDrawingApi (regla C.2) — nunca DOM propio
 * sobre el chart. Señales LONG/SHORT con `boxStyle: 'flag'` (mismo patrón
 * validado en producción que showict_quantum para señales de trade — B.2
 * es el canon de EVENTOS DE ESTRUCTURA como CHoCH/BOS, no de señales de
 * entrada; las señales de trade siguen el molde de showict_quantum). Niveles
 * TP1/TP2/liquidación de la señal activa vía addPriceLine — ninguna serie
 * toca la escala de precios (autoscaleInfoProvider ya lo gestiona el core
 * para addPriceLine).
 */
import { QUANTUM_COLORS } from './constants.js'
import { formatPriceWithPrecision } from '../../utils/pricePrecision.js'

const LABEL_FONT = "'Playfair Display', Georgia, serif"
const MAX_SIGNALS_DRAWN = 40

function isValidTime(t) {
  return t != null && t !== '' && !Number.isNaN(t)
}

function isValidPrice(v) {
  return Number.isFinite(v)
}

function withAlpha(color, alpha) {
  const a = Math.min(1, Math.max(0, alpha))
  if (typeof color !== 'string' || !color.startsWith('#')) return `rgba(0,0,0,${a})`
  const hex = color.slice(1)
  const n = parseInt(hex, 16)
  if (Number.isNaN(n) || hex.length < 6) return color
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}

// B3 (validación 2026-08-15): precisión REAL del activo (misma fuente que
// el eje nativo del chart, ver result.pricePrecision en index.js), no una
// regla de umbrales fija por magnitud — evita el desajuste "69610.95" en el
// eje vs "69611.0" en el label. `precision` es opcional (fallback a 2, igual
// que el DEFAULT_FORMAT del chart) para que los call-sites sin acceso
// directo al result sigan funcionando.
function formatPrice(v, precision) {
  return formatPriceWithPrecision(v, precision)
}

// `time` es unix seconds (TFs intradía) o 'YYYY-MM-DD' (TFs diarios/semanales
// — mismo criterio que hooks/useLiveCandle.js::normalizeTime). Mismo formato
// es-ES/day-month-hour ya usado en TradeDetailModal.jsx para consistencia.
function formatSignalDate(time) {
  if (time == null) return '—'
  const isDailyString = typeof time === 'string'
  const date = isDailyString ? new Date(`${time}T00:00:00Z`) : new Date(time * 1000)
  if (Number.isNaN(date.getTime())) return '—'
  const opts = isDailyString
    ? { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }
    : { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }
  return date.toLocaleString('es-ES', opts)
}

// V2 (validación en pantalla de Marc, 2026-08-15): detalle de la señal al
// click en su etiqueta — fecha de apertura, precio de entrada, TP1/TP2,
// liquidación/SL y estado (abierta/cerrada + motivo). Todo sale del propio
// `sig` ya resuelto por adapter.js — ningún dato adicional del chart hace
// falta, así que una señal SOLO tiene detalle si ya se está pintando (nunca
// hay un estado "detalle incompleto"): si el histórico cargado en el chart
// no llega tan atrás, la señal ni siquiera se calcula/pinta (ver
// docs/indicators/rango_quantum.md sección 8 — limitación ya documentada,
// no una nueva).
function buildSignalDetail(sig, precision) {
  const statusText =
    sig.outcome === 'tp2' ? `Cerrada — TP2 alcanzado${sig.tp1Hit ? ' (TP1 también tocado)' : ''}` :
    sig.outcome === 'sl' ? 'Cerrada — Stop Loss' :
    sig.outcome === 'liquidated' ? 'Cerrada — Liquidación' :
    sig.outcome === 'time_stop' ? `Cerrada — Time-stop${sig.tp1Hit ? ' (TP1 también tocado)' : ''}` :
    'Abierta'
  return {
    openedAt: formatSignalDate(sig.time),
    entryPrice: formatPrice(sig.entryPrice, precision),
    tp1: formatPrice(sig.tp1, precision),
    tp2: formatPrice(sig.tp2, precision),
    stopLabel: sig.slPrice != null ? 'SL' : 'Liquidación',
    stopPrice: formatPrice(sig.slPrice ?? sig.liqPrice, precision),
    status: statusText,
    closedAt: sig.outcomeTime != null ? formatSignalDate(sig.outcomeTime) : null,
  }
}

const EDGE_MARGIN_PX = 22

/**
 * Marcador de borde para un nivel (TP/SL/liquidación) cuyo precio cae fuera
 * del rango de precio VISIBLE ahora mismo. `addPriceLine` (línea nativa de
 * lightweight-charts) no ancla su etiqueta al borde del eje cuando el precio
 * está fuera de rango — se pierde en silencio (encontrado 2026-08-14,
 * validación en pantalla de Marc: señal SHORT con TP1/TP2/liquidación bien
 * calculados pero invisibles con el rango de precio de ese momento).
 *
 * B3 (validación 2026-08-15): la decisión de mostrar/ocultar y el clamp al
 * borde NO se calculan aquí. Este `render()` es un `renderOnPan: false` —
 * no vuelve a ejecutarse en cada pan/zoom (perf, ver index.js) — así que
 * clampar la posición UNA VEZ al renderizar dejaba el marcador congelado:
 * al hacer zoom out hasta que el nivel entraba en rango, la línea nativa
 * (que SÍ es una primitiva propia del chart) aparecía sola mientras el
 * marcador seguía pintado, duplicado, en su posición vieja. Se registra el
 * label con el precio REAL (nunca clampado) y `edgeClamp: true`;
 * `updateLabelPositions` (ChartPublicPage.jsx) — que SÍ corre en cada
 * cambio de rango visible (zoom/pan/resize/autoscale) — decide cada frame
 * si el nivel cae dentro del rango (oculta el marcador, la línea nativa ya
 * lo cubre) o fuera (lo clampa al borde ACTUAL con el precio real en el
 * texto).
 */
function addOffscreenLevelMarker(api, anchorTime, level, precision) {
  api.addLabel({
    time: anchorTime,
    price: level.price,
    edgeClamp: true,
    edgeMarginPx: EDGE_MARGIN_PX,
    edgeTitle: level.title,
    edgePriceText: formatPrice(level.price, precision),
    color: level.color,
    bgColor: withAlpha(level.color, 0.12),
    boxStyle: 'flag',
    font: LABEL_FONT,
  })
}

export function renderRangoQuantum(candleSeries, result, config, api) {
  if (!result || !api) return
  const cfg = config
  if (!cfg?.enabled) return

  const precision = result.pricePrecision
  const signals = Array.isArray(result.signals) ? result.signals : []
  const barTimesSet = new Set((candleSeries?.data?.() || []).map((d) => d.time))
  // Fallback si candleSeries.data() no está disponible en este contexto: no
  // filtrar por barTimesSet vacío (dejaría todo fuera) — solo se usa como
  // guarda cuando hay datos.
  const hasBarTimes = barTimesSet.size > 0

  if (cfg.showSignalLabels) {
    const toDraw = signals.slice(-MAX_SIGNALS_DRAWN)
    for (const sig of toDraw) {
      if (!isValidTime(sig.time) || !isValidPrice(sig.entryPrice)) continue
      if (hasBarTimes && !barTimesSet.has(sig.time)) continue
      const isBull = sig.direction === 'long'
      const color = isBull ? QUANTUM_COLORS.bull : QUANTUM_COLORS.bear
      const arrow = isBull ? '➹' : '➷'
      const outcomeSuffix =
        sig.outcome === 'tp2' ? ' TP2' :
        sig.outcome === 'liquidated' ? ' LIQ' :
        sig.outcome === 'sl' ? ' SL' :
        sig.outcome === 'time_stop' ? ' TS' :
        sig.tp1Hit ? ' TP1' : ''
      api.addLabel({
        time: sig.time,
        price: sig.entryPrice,
        text: `${arrow} ${isBull ? 'LONG' : 'SHORT'} ${formatPrice(sig.entryPrice, precision)}${outcomeSuffix}`,
        color,
        bgColor: withAlpha(color, 0.12),
        boxStyle: 'flag',
        isBull,
        font: LABEL_FONT,
        // Solo esta etiqueta (señal de entrada) lleva detalle — los
        // marcadores de nivel (addOffscreenLevelMarker) no representan una
        // señal propia, no lo necesitan. IndicatorOverlayLabels.jsx solo
        // activa pointer-events/click cuando `detail` está presente: el
        // resto de indicadores que comparten boxStyle:'flag' no se ven
        // afectados.
        detail: buildSignalDetail(sig, precision),
      })
    }
  }

  if (cfg.showLevels && result.activeSignal) {
    const sig = result.activeSignal
    const isBull = sig.direction === 'long'
    const tpColor = QUANTUM_COLORS.bull
    const stopColor = QUANTUM_COLORS.bear

    // Ancla del marcador de borde: la última vela visible (siempre on-screen
    // horizontalmente, sin depender de que la vela de la señal lo esté).
    const barData = candleSeries?.data?.() || []
    const anchorTime = barData.length > 0 ? barData[barData.length - 1].time : sig.time

    if (isValidPrice(sig.tp1)) {
      api.addPriceLine({
        price: sig.tp1, color: tpColor, lineWidth: 1, lineStyle: 2,
        axisLabelVisible: true, title: 'TP1 (60%)',
      })
      addOffscreenLevelMarker(api, anchorTime, { price: sig.tp1, color: tpColor, title: 'TP1' }, precision)
    }
    if (isValidPrice(sig.tp2)) {
      api.addPriceLine({
        price: sig.tp2, color: tpColor, lineWidth: 1, lineStyle: 0,
        axisLabelVisible: true, title: 'TP2',
      })
      addOffscreenLevelMarker(api, anchorTime, { price: sig.tp2, color: tpColor, title: 'TP2' }, precision)
    }
    const stopPrice = sig.slPrice ?? sig.liqPrice
    const stopTitle = sig.slPrice != null ? 'SL' : 'Liquidación'
    if (isValidPrice(stopPrice)) {
      api.addPriceLine({
        price: stopPrice, color: stopColor, lineWidth: 1, lineStyle: 2,
        axisLabelVisible: true, title: stopTitle,
      })
      addOffscreenLevelMarker(api, anchorTime, { price: stopPrice, color: stopColor, title: stopTitle }, precision)
    }
    void isBull
  }
}
